import { DayCapacity_Input, OptimizePlanRequest } from "@/api";
import { ExcelPlanResponse } from "@/api/models/ExcelPlanResponse";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Panel } from "primereact/panel";
import { useEffect, useMemo, useState } from "react";

interface SettingProps {
  response: ExcelPlanResponse | null;
  onSetting(req: OptimizePlanRequest): void;
  onBack(): void;
}

const Setting = ({response,onSetting,onBack} : SettingProps) => {
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)

  const [calendarMin,calendarMax] = useMemo(() => {
    const dates = response?.dates
      .map(date => new Date(date)).filter(Boolean) ?? []
    if (dates.length === 0) {
      return [null, null]
    }
    return [dates[0], dates[dates.length -1]]
  }, [response])

  // 2. 初期値設定（毎月21日～翌月20日）
  useEffect(() => {
    if (calendarMin && calendarMax) {
      const start = new Date(calendarMin)
      const end = new Date(calendarMin)
      end.setMonth(end.getMonth() + 1)
      end.setDate(end.getDate() - 1)
      if (end.getTime() > calendarMax.getTime()) {
        end.setTime(calendarMax.getTime())
      }
      setStartDate(start);
      setEndDate(end);
    }
  }, [calendarMin, calendarMax]);

  const simulateDays = useMemo(() => {
    // startDateからcalendarMaxまでの差分を計算
    if (startDate && endDate && calendarMax) {
      //どちらも 00:00:00 にする
      const a = new Date(startDate)
      a.setHours(0, 0, 0, 0)
      const b = new Date(endDate)
      b.setHours(0, 0, 0, 0)
      const timeDiff = b.getTime() - a.getTime();
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1;
      return daysDiff;
    }
    return 0;
  }, [startDate, endDate, calendarMax]);

  const handleBack = () => {
    onBack();
  };
  const toLocalISODate = (date: Date) => {
    // compensate timezone offset so calendar selection keeps the chosen day
    const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    return offsetDate.toISOString().split('T')[0]
  }

  const handleProceed = () => {
    if (!response || !startDate) {
      return;
    }
    const startDateStr = toLocalISODate(startDate)
    const endDateStr = endDate ? toLocalISODate(endDate) : undefined
    const startIndex = response.dates.findIndex(date => date === startDateStr)
    const endIndex = response.dates.findIndex(date => date === endDateStr)
    if (startIndex === -1 || endIndex === -1 || startIndex > endIndex) {
      return
    }

    // デフォルトの生産能力（平日8時間、土日0時間）で初期化
    const days : OptimizePlanRequest['days'] = []

    for(let i=startIndex; i<=endIndex; i++){
      const isoDate = response.dates[i]
      const date = new Date(isoDate);
      const dayOfWeek = date.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // 0:日曜日, 6:土曜日

      const capacity: DayCapacity_Input = {
        capacity: {value: isWeekend ? 0.0 : 7.0},
        lines: ['A','B','C'],
        amount: 3000,
      }

      const items: OptimizePlanRequest['days'][number]['items'] = {}
      response.itemMaster.forEach(item => {
        const production = item.生産[i]
        const shipment = item.出荷[i]
        const inventory = item.在庫[i]
        items[item.品目ID] = {
          production: production ? { value: production.value, is_bold: production.is_bold } : { value: 0, is_bold: false },
          shipment: shipment ? { value: shipment.value } : undefined,
          inventory: inventory ? { value: inventory.value } : undefined,
        }
      })

      days.push({
        date: isoDate,
        capacity,
        processTime: { value: 0 },
        items,
      })
    }

    const plan : OptimizePlanRequest = {
      days,
      optimize: OptimizePlanRequest.optimize.SIMULATION_ONLY
    }
    onSetting(plan);
  };
  return (
    <Panel header="範囲・日程の設定" className="w-full">
        {(calendarMax && calendarMin) && (
        <div>
          <p>
            Excel上の日付範囲: {calendarMin.toLocaleDateString()} 〜 {calendarMax.toLocaleDateString()}
          </p>
          <p>選択された日付範囲: {startDate ? startDate.toLocaleDateString() : ''} 〜 {endDate ? endDate.toLocaleDateString() : ''}</p>
          <p>選択された対象日数: {simulateDays} </p>
          <div className="flex flex-row justify-content-around align-items-center w-full gap-1">
            <Calendar value={startDate} onChange={(e) => setStartDate(e.value ?? null)}
              minDate={new Date(calendarMin.getTime() + 3600*24*1000)}  //初日は選べないようにする
              maxDate={calendarMax} readOnlyInput dateFormat="yy/mm/dd"
              inline
              locale="ja"/>
            <p className="text-2xl">→</p>
            <Calendar value={endDate} onChange={(e) => setEndDate(e.value ?? null)}
              minDate={new Date(calendarMin.getTime() + 3600*24*1000)}  //初日は選べないようにする
              maxDate={calendarMax} readOnlyInput dateFormat="yy/mm/dd"
              inline
              locale="ja"/>
          </div>
        </div>
        )}

        <div className="flex flex-row justify-content-between mt-6">
        <Button label="戻る" icon="pi pi-arrow-left"
          iconPos="left" size="small" severity='secondary'
          onClick={handleBack} />
        <Button label="進む" icon="pi pi-arrow-right"
          iconPos="right" size="small" severity='success'
          disabled={response === null}
          onClick={handleProceed} />
      </div>
    </Panel>
  );
}
export default Setting;
